import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { 
    getMyStore, 
    getStoreSchedule, 
    addStoreSchedule, 
    updateStoreSchedule, 
    deleteStoreSchedule, 
    getCardgames 
} from '../../services/supabaseService';

const DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

export const StoreSchedule: React.FC = () => {
    const { user } = useAuth();
    const { showNotification } = useNotification();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [store, setStore] = useState<any>(null);
    const [schedule, setSchedule] = useState<any[]>([]);
    const [games, setGames] = useState<any[]>([]);

    const [newSchedule, setNewSchedule] = useState({
        game_id: '',
        dia: 'Segunda',
        horario: '19:00',
        valor_insc: '0',
        observacao: ''
    });

    const [editingId, setEditingId] = useState<number | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

    useEffect(() => {
        if (user && user.role_id === 6) {
            fetchData();
        } else if (user) {
            navigate('/');
        }
    }, [user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const myStore = await getMyStore();
            if (myStore) {
                setStore(myStore);
                const [scheduleData, gamesData] = await Promise.all([
                    getStoreSchedule(myStore.id),
                    getCardgames()
                ]);
                setSchedule(scheduleData || []);
                setGames(gamesData || []);
            }
        } catch (error) {
            showNotification("Erro ao carregar agenda da loja", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSelectEdit = (item: any) => {
        setEditingId(item.id);
        setNewSchedule({
            game_id: String(item.game_id || ''),
            dia: item.dia,
            horario: item.horario,
            valor_insc: String(item.valor_insc || '0'),
            observacao: item.observacao || ''
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setNewSchedule({
            game_id: '',
            dia: 'Segunda',
            horario: '19:00',
            valor_insc: '0',
            observacao: ''
        });
    };

    const handleSubmitSchedule = async (e: React.FormEvent) => {
        e.preventDefault();
        const fee = parseFloat(newSchedule.valor_insc);
        if (isNaN(fee) || fee < 0) {
            showNotification("A taxa de inscrição não pode ser negativa", "error");
            return;
        }
        if (!newSchedule.game_id) {
            showNotification("Selecione um jogo para o evento", "error");
            return;
        }

        setSaving(true);
        try {
            if (editingId) {
                const success = await updateStoreSchedule(editingId, {
                    ...newSchedule,
                    valor_insc: fee,
                    game_id: Number(newSchedule.game_id)
                });
                if (success) {
                    showNotification("Horário da agenda atualizado com sucesso!", "success");
                    handleCancelEdit();
                    fetchData();
                } else {
                    showNotification("Erro ao atualizar horário", "error");
                }
            } else {
                const success = await addStoreSchedule({
                    ...newSchedule,
                    store_id: store.id,
                    valor_insc: fee,
                    game_id: Number(newSchedule.game_id)
                });
                if (success) {
                    showNotification("Novo horário adicionado à agenda!", "success");
                    handleCancelEdit();
                    fetchData();
                } else {
                    showNotification("Erro ao adicionar horário", "error");
                }
            }
        } catch (error) {
            showNotification("Erro ao salvar horário", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteSchedule = async (id: number) => {
        try {
            const success = await deleteStoreSchedule(id);
            if (success) {
                showNotification("Horário removido com sucesso!", "success");
                if (editingId === id) {
                    handleCancelEdit();
                }
                setDeleteConfirmId(null);
                fetchData();
            } else {
                showNotification("Erro ao remover horário da agenda", "error");
            }
        } catch (error) {
            showNotification("Erro ao remover horário da agenda", "error");
        }
    };

    if (loading) {
        return (
            <div className="p-12 text-center text-slate-500 font-bold uppercase tracking-widest text-xs animate-pulse">
                Carregando agenda semanal...
            </div>
        );
    }

    if (!store) {
        return (
            <div className="p-12 text-center text-slate-400 font-bold text-sm">
                Loja não encontrada ou permissão insuficiente.
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto space-y-6 animate-in fade-in duration-300">
            {/* Header */}
            <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-800">
                <div>
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-400 mb-1">
                        <Link to="/minha-loja" className="hover:text-purple-400 transition-colors">Configurações</Link>
                        <span>/</span>
                        <span className="text-white">Agenda Semanal</span>
                    </div>
                    <h1 className="text-2xl font-black text-white uppercase tracking-tight flex items-center gap-3">
                        <i className="fas fa-calendar-days text-emerald-500 text-xl"></i>
                        Agenda Semanal
                    </h1>
                    <p className="text-slate-400 text-xs mt-0.5">
                        Cadastre os dias e horários fixos de encontros, ligas e torneios semanais para seus clientes.
                    </p>
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Form to Add / Edit */}
                <div className="lg:col-span-1 order-2 lg:order-1">
                    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 sm:p-6 sticky top-4">
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-800">
                            <h2 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                                <i className={`fas ${editingId ? 'fa-edit text-purple-400' : 'fa-plus text-emerald-400'} text-xs`}></i>
                                {editingId ? 'Editar Atividade' : 'Nova Atividade'}
                            </h2>
                            {editingId && (
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="text-[10px] text-slate-400 hover:text-white font-bold uppercase transition-colors"
                                >
                                    Cancelar
                                </button>
                            )}
                        </div>

                        <form onSubmit={handleSubmitSchedule} className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Jogo</label>
                                <select 
                                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2 text-xs text-white focus:border-emerald-500 outline-none transition-all"
                                    value={newSchedule.game_id}
                                    onChange={(e) => setNewSchedule({...newSchedule, game_id: e.target.value})}
                                    required
                                >
                                    <option value="">Selecione o Card Game</option>
                                    {games.map(g => (
                                        <option key={g.id} value={g.id}>{g.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Dia da Semana</label>
                                <select 
                                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2 text-xs text-white focus:border-emerald-500 outline-none transition-all"
                                    value={newSchedule.dia}
                                    onChange={(e) => setNewSchedule({...newSchedule, dia: e.target.value})}
                                >
                                    {DAYS.map(d => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Horário</label>
                                    <input 
                                        type="time"
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white uppercase color-scheme-dark focus:border-emerald-500 outline-none"
                                        value={newSchedule.horario}
                                        onChange={(e) => setNewSchedule({...newSchedule, horario: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Taxa (R$)</label>
                                    <input 
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0,00"
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:border-emerald-500 outline-none"
                                        value={newSchedule.valor_insc}
                                        onChange={(e) => setNewSchedule({...newSchedule, valor_insc: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Observações / Formato</label>
                                <input 
                                    type="text"
                                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2 text-xs text-white placeholder:text-slate-600 focus:border-emerald-500 outline-none"
                                    placeholder="Ex: Formato Standard, Premiação em Boosters"
                                    value={newSchedule.observacao}
                                    onChange={(e) => setNewSchedule({...newSchedule, observacao: e.target.value})}
                                />
                            </div>

                            <div className="pt-2">
                                <button 
                                    type="submit"
                                    disabled={saving}
                                    className={`w-full text-white font-black py-2.5 rounded-xl text-xs uppercase tracking-wider active:scale-95 transition-all shadow-md flex items-center justify-center gap-2 ${
                                        editingId 
                                            ? 'bg-purple-600 hover:bg-purple-500 shadow-purple-900/20' 
                                            : 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-900/20'
                                    }`}
                                >
                                    {saving ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin text-xs"></i>
                                            Salvando...
                                        </>
                                    ) : (
                                        <>
                                            <i className={`fas ${editingId ? 'fa-check' : 'fa-plus'} text-xs`}></i>
                                            {editingId ? 'Salvar Edição' : 'Adicionar à Agenda'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                {/* List of Scheduled Events */}
                <div className="lg:col-span-2 order-1 lg:order-2 space-y-4">
                    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 sm:p-6">
                        <div className="flex items-center justify-between pb-3 border-b border-slate-800 mb-4">
                            <h2 className="text-sm font-black text-white uppercase tracking-wider">
                                Atividades Cadastradas ({schedule.length})
                            </h2>
                            <span className="text-[11px] text-slate-400 font-medium">
                                Atualizado em tempo real na página pública
                            </span>
                        </div>

                        {schedule.length === 0 ? (
                            <div className="text-center py-12 bg-slate-950/40 rounded-xl border border-dashed border-slate-800">
                                <i className="fas fa-calendar-xmark text-3xl text-slate-700 mb-3 block"></i>
                                <p className="text-slate-400 font-bold text-sm">Nenhum horário fixo cadastrado ainda.</p>
                                <p className="text-slate-500 text-xs mt-1">Preencha o formulário ao lado para adicionar o primeiro dia de torneio ou encontro.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {schedule.map((item, idx) => (
                                    <div 
                                        key={item.id || idx} 
                                        className="bg-slate-950/60 border border-slate-800 p-4 rounded-xl flex items-center justify-between group hover:border-slate-700 transition-all"
                                    >
                                        <div className="flex items-center space-x-3.5 min-w-0">
                                            <div className="bg-slate-800 text-emerald-400 border border-slate-700 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase shrink-0">
                                                {item.dia}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-xs font-bold text-white uppercase truncate">
                                                    {item.game_name || item.jogo}
                                                </p>
                                                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-400">
                                                    <span className="font-mono text-slate-300 font-semibold">{item.horario}</span>
                                                    <span>•</span>
                                                    <span>
                                                        Inscrição: {Number(item.valor_insc) > 0 ? `R$ ${Number(item.valor_insc).toFixed(2)}` : 'Gratuita'}
                                                    </span>
                                                    {item.observacao && (
                                                        <>
                                                            <span>•</span>
                                                            <span className="text-slate-400 italic truncate max-w-xs">{item.observacao}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-2 shrink-0 ml-3">
                                            {deleteConfirmId === item.id ? (
                                                <div className="flex items-center space-x-1.5 animate-in fade-in zoom-in-95 duration-200">
                                                    <span className="text-[10px] font-black text-rose-400 uppercase tracking-wider mr-1">Remover?</span>
                                                    <button 
                                                        onClick={() => handleDeleteSchedule(item.id)}
                                                        className="bg-rose-600 hover:bg-rose-500 text-white text-[10px] font-black px-2.5 py-1 rounded-md uppercase tracking-wider transition-all"
                                                    >
                                                        Sim
                                                    </button>
                                                    <button 
                                                        onClick={() => setDeleteConfirmId(null)}
                                                        className="bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] font-black px-2 py-1 rounded-md uppercase tracking-wider transition-all"
                                                    >
                                                        Não
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center space-x-1">
                                                    <button 
                                                        onClick={() => handleSelectEdit(item)}
                                                        className="p-2 text-slate-400 hover:text-purple-400 transition-colors rounded-lg hover:bg-slate-800/60"
                                                        title="Editar horário"
                                                    >
                                                        <i className="fas fa-edit text-xs"></i>
                                                    </button>
                                                    <button 
                                                        onClick={() => setDeleteConfirmId(item.id)}
                                                        className="p-2 text-slate-400 hover:text-rose-400 transition-colors rounded-lg hover:bg-slate-800/60"
                                                        title="Remover horário"
                                                    >
                                                        <i className="fas fa-trash-alt text-xs"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
